import { DatePipe, TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import moment from 'moment';

import {
  ChartDataset,
  ChartDatasetOption,
  ChartLabel,
} from '../entities/chart-dataset';
import { ChartDataFilterPoint } from '../entities/chart-data-filter-point.entity';
import { ChartDataPoint } from '../entities/chart-data-point.entity';
import { ChartFeature } from '../entities/chart-feature';
import { Feature } from '../entities/feature.entity';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  constructor(
    private datePipe: DatePipe,
    private titleCasePipe: TitleCasePipe
  ) {}

  /**
   * Sets list of values
   * @param origin
   * @param item
   * @returns list
   */
  private setList(
    origin: ChartDataFilterPoint,
    item: ChartDataFilterPoint
  ): any {
    origin.value = [...new Set(origin.value.concat(item.value))];

    return origin;
  }

  /**
   * Calcules total of chart data point
   * @param origin
   * @param item
   * @returns total
   */
  private setTotal(origin: ChartDataPoint, item: ChartDataPoint): any {
    origin.value = origin.value + item.value;

    return origin;
  }

  /**
   * Gets date extremes
   * @param chartDataPoints
   * @param clipped
   * @returns Date list
   */
  private getEnds(
    chartDataPoints: ChartDataPoint[],
    clipped: boolean = true
  ): Date[] {
    chartDataPoints.sort((a, b) => {
      return (a.date as Date).getTime() - (b.date as Date).getTime();
    });

    if (clipped) {
      return [
        chartDataPoints[0].date,
        chartDataPoints[chartDataPoints.length - 1].date,
      ];
    }

    return [
      moment(chartDataPoints[0].date)
        .set({ hour: 0, minute: 0, second: 0 })
        .startOf('month')
        .toDate(),
      moment(chartDataPoints[chartDataPoints.length - 1].date)
        .set({ hour: 0, minute: 0, second: 0 })
        .endOf('month')
        .toDate(),
    ];
  }

  /**
   * Defines x axis with dates
   * @param chartDataPoints
   * @param clipped
   * @returns chart labels list
   */
  public setXLabels(
    chartDataPoints: ChartDataPoint[],
    clipped: boolean = true
  ): ChartLabel[] {
    let xLabels = [];

    if (chartDataPoints.length == 0) {
      return [];
    }

    // Get min/max
    let dateEnds = this.getEnds(chartDataPoints, clipped);

    let incrementDate = moment(dateEnds[0]).set({
      hour: 0,
      minute: 0,
      second: 0,
    });
    let endDate = moment(dateEnds[1]).set({ hour: 0, minute: 0, second: 0 });
    let lastMonth = '';
    while (incrementDate.isSameOrBefore(endDate)) {
      let key = '';
      let title = '';

      key = this.datePipe.transform(incrementDate.toDate(), 'yyyy-MM-dd');
      if (lastMonth == '' || lastMonth != incrementDate.format('YYYY-MM')) {
        lastMonth = incrementDate.format('YYYY-MM');
        title = this.titleCasePipe.transform(
          this.datePipe.transform(incrementDate.toDate(), 'MM/yyyy')
        );
      }

      const firstChartDataPointItem = chartDataPoints.find(
        (item) => key == this.datePipe.transform(item.date, 'yyyy-MM-dd')
      );
      xLabels.push({
        key: this.datePipe.transform(
          firstChartDataPointItem !== undefined
            ? firstChartDataPointItem.date
            : incrementDate.toDate(),
          'yyyy-MM-dd HH:mm:ss'
        ),
        title: title,
      });

      const chartDataPointItems = chartDataPoints.filter(
        (item) =>
          key == this.datePipe.transform(item.date, 'yyyy-MM-dd') &&
          (firstChartDataPointItem === undefined ||
            (firstChartDataPointItem !== undefined &&
              firstChartDataPointItem.date !== item.date))
      );
      chartDataPointItems.forEach((chartDataPointItem) => {
        let dateStr = this.datePipe.transform(
          chartDataPointItem.date,
          'yyyy-MM-dd HH:mm:ss'
        );
        if (!xLabels.find((item) => item.key == dateStr)) {
          xLabels.push({
            key: dateStr,
            title: '',
          });
        }
      });

      incrementDate.add(1, 'd');
    }

    return xLabels;
  }

  /**
   * Defines dataset for chartjs
   * @param label
   * @param chartDataPoints
   * @param chartDatasetOption
   * @param chartDataFilterPoints
   * @param xLabels
   * @param min
   * @param max
   * @param isMaxLimited
   * @param isMinLimited
   * @param chartDataMaxPoints
   * @returns chart dataset
   */
  public setDataset(
    feature: ChartFeature,
    chartDataPoints: ChartDataPoint[],
    chartDatasetOption: ChartDatasetOption,
    chartDataFilterPoints: ChartDataFilterPoint[],
    xLabels: ChartLabel[],
    min: number,
    max: number,
    isMaxLimited: boolean = false,
    isMinLimited: boolean = false,
    chartDataMaxPoints: ChartDataPoint[] = [],
    assignMax: boolean = false
  ): ChartDataset {
    let chartData = [];
    let tickModel;

    // Set tick model
    if (chartDatasetOption.useTickModel && chartDatasetOption.tooltip) {
      if (chartDatasetOption.tooltip.pattern) {
        tickModel = {
          pattern: chartDatasetOption.tooltip.pattern,
          replace: chartDatasetOption.tooltip.replace,
        };
      }
    }

    xLabels.forEach((xLabel) => {
      let dataItem: ChartDataPoint;
      let filterItem: ChartDataFilterPoint;
      let maxItem: ChartDataPoint;
      let tooltip: any = {
        label: '',
        body: '',
      };

      dataItem = chartDataPoints.find(
        (item) =>
          xLabel.key ==
          this.datePipe.transform(item.date, 'yyyy-MM-dd HH:mm:ss')
      );
      filterItem = chartDataFilterPoints.find(
        (item) =>
          xLabel.key ==
          this.datePipe.transform(item.date, 'yyyy-MM-dd HH:mm:ss')
      );
      maxItem = chartDataMaxPoints.find(
        (item) =>
          xLabel.key ==
          this.datePipe.transform(item.date, 'yyyy-MM-dd HH:mm:ss')
      );

      // Set value
      let value: string | number = null;
      let tooltipValue = '';
      const nbDecimalDigits =
        chartDatasetOption.nbDecimalDigits !== undefined
          ? chartDatasetOption.nbDecimalDigits
          : 2;
      if (dataItem !== undefined) {
        const round =
          nbDecimalDigits == 0
            ? Math.pow(10, 1)
            : Math.pow(10, nbDecimalDigits);

        // Set max item
        if (maxItem !== undefined && Boolean(maxItem.value) && assignMax) {
          dataItem.value = max;
        }

        if (isMaxLimited && max && dataItem.value > max) {
          value = max;
        } else if (isMinLimited && min && dataItem.value < min) {
          value = min;
        } else {
          value = dataItem.value;
        }

        value = (Math.round(value * round) / round).toFixed(nbDecimalDigits);
        tooltipValue = (Math.round(dataItem.value * round) / round).toFixed(
          nbDecimalDigits
        );
      }

      // Set value for tooltip
      if (tooltipValue) {
        if (chartDatasetOption.tooltip) {
          if (chartDatasetOption.tooltip.pattern) {
            tooltipValue = String(tooltipValue).replace(
              chartDatasetOption.tooltip.pattern,
              chartDatasetOption.tooltip.replace
            );
          }
        }
      }

      // Set tooltip
      tooltip = {
        title: this.titleCasePipe.transform(
          this.datePipe.transform(
            moment(xLabel.key, 'YYYY-MM-DD HH:mm:ss').toDate(),
            'dd/MM/yyyy'
          )
        ),
        body:
          dataItem !== undefined
            ? chartDatasetOption.tooltip
              ? chartDatasetOption.tooltip.label +
                ' ' +
                tooltipValue +
                ' ' +
                chartDatasetOption.tooltip.unit
              : tooltipValue
            : '',
      };

      chartData.push({
        tooltip: tooltip,
        value: value,
        filteredBy: filterItem !== undefined ? filterItem.value : null,
      });
    });

    return {
      id: feature.featureId,
      type: chartDatasetOption.type,
      hidden: chartDatasetOption.hidden ?? false,
      data: chartData,
      label: feature.label,
      color: chartDatasetOption.color,
      axis: chartDatasetOption.axis,
      tickModel: tickModel,
      pointStyle: chartDatasetOption.pointStyle,
      min: min,
      max: max,
      nbSteps: chartDatasetOption.nbSteps,
      nbDecimalDigits: chartDatasetOption.nbDecimalDigits,
      isMaxLimited: isMaxLimited,
      isMinLimited: isMinLimited,
    };
  }

  /**
   * Gets progression between two extreme values
   * @param points
   * @param calculateDifference
   * @returns progression
   */
  public getProgression(
    points: any[] = [],
    calculateDifference: boolean = false
  ): number {
    points = points.filter((item) => item.value !== null);

    if (points.length > 0) {
      const lastValue = points[points.length - 1].value;
      const firstValue = points[0].value;

      if (calculateDifference) {
        return Math.round((lastValue - firstValue) * 100) / 100;
      }

      return (
        Math.round(((lastValue - firstValue) / Math.abs(firstValue)) * 10000) /
        100
      );
    }

    return 0;
  }

  /**
   * Defines data points from features
   * @param groupedFeatures
   * @returns chart data points list
   */
  public setChartDataPoints(groupedFeatures: Feature[][]): ChartDataPoint[] {
    return Object.keys(groupedFeatures).reduce((origin, item) => {
      let chartDataPoint: ChartDataPoint;

      chartDataPoint = groupedFeatures[item]
        .map((o) =>
          new ChartDataPoint().deserialize({
            id: o.getId(),
            name: o.featureName,
            date: o.startDate,
            type: o.featureType,
            value: o.featureValue,
            unit: o.featureUnit,
            min: o.featureMin,
            max: o.featureMax,
          })
        )
        .reduce(this.setTotal.bind(this));
      chartDataPoint.value =
        chartDataPoint.value / groupedFeatures[item].length;

      origin.push(chartDataPoint);

      return origin;
    }, []);
  }

  /**
   * Defines data filter points from features
   * @param groupedFeatures
   * @returns chart filter points list
   */
  public setChartDataFilterPoints(
    groupedFeatures: Feature[][]
  ): ChartDataFilterPoint[] {
    return Object.keys(groupedFeatures).reduce((origin, item) => {
      let chartDataFilterPoint: ChartDataFilterPoint;

      chartDataFilterPoint = groupedFeatures[item]
        .map((o) =>
          new ChartDataFilterPoint().deserialize({
            id: o.getId(),
            name: o.featureName,
            date: o.startDate,
            value: [o.featureValue],
          })
        )
        .reduce(this.setList.bind(this));

      origin.push(chartDataFilterPoint);

      return origin;
    }, []);
  }
}
